import { chamar } from './api';
import { ehNativo } from './nativo';

/**
 * Portal do Cliente (Onda 1): avisos no aparelho por trás de uma abstração. Hoje, Web Push (VAPID, o mesmo do MalgoOS) no
 * navegador e no app instalado (PWA); no app das lojas, o provedor nativo (APNs no iOS e no Mac, FCM no Android, pelo
 * plugin `@capacitor/push-notifications`) entra pelo mesmo registro de aparelho (`tipo` apns ou fcm), sem mudar as telas.
 */
export type SuportePush = 'sim' | 'nao' | 'ios_instalar' | 'negado';

export interface ProvedorPush {
  suporte(): SuportePush;
  ativo(): Promise<boolean>;
  ativar(chavePublica: string): Promise<'ok' | 'negado' | 'erro'>;
  desativar(): Promise<void>;
  /** Endereço da inscrição deste aparelho (o sair o leva para o servidor tirar do push), ou nulo. */
  endpoint(): Promise<string | null>;
  /** Solta a inscrição só neste aparelho (o servidor já foi avisado pelo sair). */
  soltar(): Promise<void>;
}

/**
 * Review r23 segurança (O3): sair do portal tira este aparelho dos avisos. O endereço da inscrição vai no próprio sair (o
 * servidor marca o aparelho como removido na mesma requisição, antes de encerrar a sessão) e a inscrição é solta aqui; o
 * aparelho compartilhado não segue recebendo os avisos do cliente.
 */
export async function sairDoPortal(d: { push: Pick<ProvedorPush, 'endpoint' | 'soltar'>; sair: (corpo: { endpoint?: string }) => Promise<unknown>; apagarToken: () => Promise<void> }) {
  const endpoint = await d.push.endpoint().catch(() => null);
  await d.sair(endpoint ? { endpoint } : {}).catch(() => undefined);
  await d.push.soltar().catch(() => undefined);
  await d.apagarToken().catch(() => undefined);
}

/**
 * Review r24 segurança (O5): "sair de todos os outros aparelhos" também tira os outros aparelhos da conta do push. O
 * endereço da inscrição DESTE aparelho vai junto para ele seguir recebendo os avisos; sem inscrição, vai sem endereço.
 */
export async function sairDeTodos<T>(d: { push: Pick<ProvedorPush, 'endpoint'>; enviar: (corpo: { endpoint?: string }) => Promise<T> }): Promise<T> {
  const endpoint = await d.push.endpoint().catch(() => null);
  return d.enviar(endpoint ? { endpoint } : {});
}

const daBase64Url = (s: string) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));

export function nomeDoAparelho(ua: string = navigator.userAgent) {
  const so = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : 'Linux';
  const nav = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : '';
  return [so, nav].filter(Boolean).join(' ');
}

export function registrarServiceWorker() {
  if (ehNativo() || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
}

const webPush: ProvedorPush = {
  suporte() {
    const nav = navigator as Navigator & { standalone?: boolean };
    const ios = /iPhone|iPad|iPod/.test(nav.userAgent);
    const instalado = nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true;
    if (ios && !instalado) return 'ios_instalar';
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'nao';
    return Notification.permission === 'denied' ? 'negado' : 'sim';
  },
  async ativo() {
    if (this.suporte() !== 'sim') return false;
    const reg = await navigator.serviceWorker.getRegistration('/');
    return !!(await reg?.pushManager.getSubscription());
  },
  async ativar(chavePublica) {
    if ((await Notification.requestPermission()) !== 'granted') return 'negado';
    const reg = await navigator.serviceWorker.ready;
    const inscricao = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: daBase64Url(chavePublica) }));
    const j = inscricao.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (!j.endpoint || !j.keys?.p256dh || !j.keys.auth) return 'erro';
    await chamar('POST', '/dispositivos', { tipo: 'webpush', endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth, nome: nomeDoAparelho() });
    return 'ok';
  },
  async desativar() {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const s = await reg?.pushManager.getSubscription();
    if (!s) return;
    await chamar('POST', '/dispositivos/remover', { endpoint: s.endpoint }).catch(() => undefined);
    await s.unsubscribe().catch(() => undefined);
  },
  async endpoint() {
    if (!('serviceWorker' in navigator)) return null;
    const reg = await navigator.serviceWorker.getRegistration('/');
    return (await reg?.pushManager.getSubscription())?.endpoint ?? null;
  },
  async soltar() {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration('/');
    await (await reg?.pushManager.getSubscription())?.unsubscribe();
  },
};

/** No app das lojas, até o plugin nativo entrar no empacotamento (Onda 2), os avisos ficam no e-mail. */
const nativoPendente: ProvedorPush = {
  suporte: () => 'nao',
  ativo: async () => false,
  ativar: async () => 'erro',
  desativar: async () => undefined,
  endpoint: async () => null,
  soltar: async () => undefined,
};

export const push: ProvedorPush = ehNativo() ? nativoPendente : webPush;
